import { useCallback, useState } from "react";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import { toast } from "react-toastify";

import {
  cleanUrl,
  formatPhoneFR,
  getAssetUrl,
  getDisplaySiteUrl,
  safeFileName,
} from "../utils/memberSignupPosterUtils";

const COLORS = {
  blue: "#052b72",
  blue2: "#0a4fe8",
  orange: "#f97316",
  orange2: "#ea580c",
  dark: "#061b46",
  text: "#13284b",
  muted: "#5c6f91",
  border: "#dbe6f5",
  softBlue: "#eaf2ff",
  softOrange: "#fff3e8",
  white: "#ffffff",
};

const getFeatures = (settings = {}) => {
  const features = [
    { label: "Inscription en ligne avec documents", show: true },
    { label: "Paiement en ligne des cotisations", show: true },
    {
      label: "Accès à la boutique du club",
      show: settings.display_boutique || settings.display_commandes,
    },
    {
      label: "Calendrier des événements et cours d’essai",
      show: settings.display_calendrier || settings.display_evenements,
    },
    {
      label: "Inscriptions aux événements et compétitions",
      show: settings.display_evenements || settings.display_suivicompetition,
    },
    {
      label: "Lexique, règles et arbitrage",
      show: settings.display_lexique || settings.display_arbitrage,
    },
    {
      label: "Techniques, quiz et progression",
      show: settings.display_techniques || settings.display_techniquesquizz,
    },
    {
      label: "Actualités et galerie photo",
      show: settings.display_news || settings.display_galerie,
    },
    {
      label: "Messagerie et chat du club",
      show: settings.display_chat,
    },
    {
      label: "Préparation physique personnalisée",
      show:
        settings.display_prepa_physique ||
        settings.display_prepa_physique_coaching,
    },
  ];

  // 9 fonctionnalités maximum pour éviter le débordement dans le cadre PDF
  return features.filter((feature) => feature.show).slice(0, 9);
};

const addRoundedRect = (pdf, x, y, w, h, radius, fillColor, strokeColor) => {
  pdf.setFillColor(fillColor);
  pdf.setDrawColor(strokeColor || fillColor);
  pdf.roundedRect(x, y, w, h, radius, radius, "FD");
};

const addText = (
  pdf,
  text,
  x,
  y,
  {
    size = 10,
    color = COLORS.text,
    fontStyle = "normal",
    maxWidth = null,
    lineHeight = 1.15,
    align = "left",
  } = {},
) => {
  pdf.setFont("helvetica", fontStyle);
  pdf.setFontSize(size);
  pdf.setTextColor(color);

  if (maxWidth) {
    const lines = pdf.splitTextToSize(String(text || ""), maxWidth);

    pdf.text(lines, x, y, {
      align,
      lineHeightFactor: lineHeight,
    });

    return lines.length;
  }

  pdf.text(String(text || ""), x, y, { align });
  return 1;
};

const addBrandLine = (pdf, x, y) => {
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(10.5);

  pdf.setTextColor(COLORS.blue);
  pdf.text("MonAppli", x, y);

  const monAppliWidth = pdf.getTextWidth("MonAppli");

  pdf.setTextColor(COLORS.orange);
  pdf.text("Club", x + monAppliWidth + 1.2, y);

  pdf.setTextColor(COLORS.muted);
  pdf.setFontSize(8.5);
  pdf.text("Espace adhérent", x + monAppliWidth + 18, y);
};

const loadImageAsPngDataUrl = async (src) => {
  if (!src) return null;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);

        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        console.warn("Logo non exportable dans le PDF :", error);
        resolve(null);
      }
    };

    img.onerror = () => resolve(null);
    img.src = src;
  });
};

const drawLogoFallback = (pdf, settings, x, y, size) => {
  const source =
    settings?.shortname_club ||
    settings?.nom_club ||
    settings?.commune_club ||
    "Club";

  const initials = String(source)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join("");

  pdf.setFillColor(COLORS.blue);
  pdf.circle(x + size / 2, y + size / 2, size / 2, "F");

  pdf.setFillColor(COLORS.orange);
  pdf.circle(x + size - 6, y + 7, 6, "F");

  addText(pdf, initials || "CL", x + size / 2, y + size / 2 + 4, {
    size: 15,
    color: COLORS.white,
    fontStyle: "bold",
    align: "center",
  });
};

const drawInfoLine = (pdf, label, value, x, y, maxWidth = 105) => {
  if (!value) return 0;

  const fullText = `${label} : ${value}`;
  const lines = pdf.splitTextToSize(fullText, maxWidth);
  const labelText = `${label} :`;

  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(7.5);
  pdf.setTextColor(COLORS.blue);

  if (lines.length === 1) {
    pdf.text(labelText, x, y);

    const labelWidth = pdf.getTextWidth(labelText);

    pdf.setTextColor(COLORS.text);
    pdf.text(String(value), x + labelWidth + 2, y);
  } else {
    pdf.text(lines, x, y, {
      lineHeightFactor: 1.12,
    });
  }

  return Math.max(5.4, lines.length * 4.3);
};

const drawFeatureRow = (pdf, index, label, x, y, width) => {
  const rowHeight = 7.6;

  pdf.setDrawColor(COLORS.border);
  pdf.line(x, y + rowHeight - 1.2, x + width, y + rowHeight - 1.2);

  pdf.setFillColor(COLORS.softBlue);
  pdf.roundedRect(x, y - 3, 6.4, 6.4, 1.5, 1.5, "F");

  addText(pdf, String(index + 1), x + 3.2, y + 1.3, {
    size: 5.8,
    color: COLORS.blue,
    fontStyle: "bold",
    align: "center",
  });

  addText(pdf, label, x + 9.5, y + 1.2, {
    size: 7,
    color: COLORS.blue,
    fontStyle: "bold",
    maxWidth: width - 11,
  });
};

export default function useMemberSignupPosterPdf({
  settings,
  registerUrl,
  siteUrl,
}) {
  const [isGeneratingPoster, setIsGeneratingPoster] = useState(false);

  const generatePosterPdf = useCallback(async () => {
    const finalRegisterUrl = registerUrl || siteUrl;

    if (!finalRegisterUrl) {
      toast.error("Aucune URL d’inscription n’est configurée.");
      return;
    }

    try {
      setIsGeneratingPoster(true);

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pageW = 210;
      const pageH = 297;

      const clubName = settings?.nom_club || "Votre club";
      const shortName = settings?.shortname_club || clubName;
      const season = settings?.saison_active || "";

      const displaySiteUrl = getDisplaySiteUrl(
        settings?.siteweb_club || siteUrl,
      );

      const registerDisplayUrl = `${cleanUrl(displaySiteUrl)}/`;
      const features = getFeatures(settings);

      const qrDataUrl = await QRCode.toDataURL(finalRegisterUrl, {
        errorCorrectionLevel: "H",
        margin: 2,
        width: 900,
        color: {
          dark: "#000000",
          light: "#ffffff",
        },
      });

      const logoUrl = getAssetUrl(settings?.logo_club);
      const logoDataUrl = await loadImageAsPngDataUrl(logoUrl);

      // Fond
      pdf.setFillColor("#f8fbff");
      pdf.rect(0, 0, pageW, pageH, "F");

      pdf.setFillColor("#fff3e8");
      pdf.circle(190, 6, 40, "F");

      pdf.setFillColor("#eaf2ff");
      pdf.circle(-10, 182, 42, "F");

      // HEADER
      addRoundedRect(pdf, 12, 12, 28, 28, 7, COLORS.white, COLORS.border);

      if (logoDataUrl) {
        pdf.addImage(logoDataUrl, "PNG", 15, 15, 22, 22);
      } else {
        drawLogoFallback(pdf, settings, 15, 15, 22);
      }

      addText(pdf, clubName, 46, 24, {
        size: clubName.length > 25 ? 21 : 24,
        color: COLORS.blue,
        fontStyle: "bold",
        maxWidth: 145,
      });

      addBrandLine(pdf, 46, 34.5);

      // HERO
      addRoundedRect(pdf, 12, 48, 186, 42, 8, COLORS.white, COLORS.border);

      pdf.setFillColor(COLORS.blue2);
      pdf.roundedRect(20, 56, 43, 8, 4, 4, "F");

      addText(pdf, "INSCRIPTION EN LIGNE", 24, 61.4, {
        size: 6.3,
        color: COLORS.white,
        fontStyle: "bold",
      });

      addText(pdf, "Créez votre compte", 20, 75, {
        size: 19.5,
        color: COLORS.blue,
        fontStyle: "bold",
      });

      addText(pdf, "et inscrivez-vous au club", 20, 84, {
        size: 19.5,
        color: COLORS.blue,
        fontStyle: "bold",
      });

      pdf.setFillColor(COLORS.orange);
      pdf.roundedRect(20, 87.5, 32, 1.7, 1, 1, "F");

      addText(
        pdf,
        "Une application simple pour suivre la vie du club, gérer les inscriptions et rester informé toute la saison.",
        92,
        66,
        {
          size: 8.8,
          color: COLORS.muted,
          fontStyle: "bold",
          maxWidth: 82,
          lineHeight: 1.18,
        },
      );

      // STRUCTURE
      const leftX = 12;
      const rightX = 143;
      const leftW = 121;
      const rightW = 55;

      // CARD INFOS CLUB
      addRoundedRect(
        pdf,
        leftX,
        100,
        leftW,
        50,
        7,
        COLORS.white,
        COLORS.border,
      );

      addText(pdf, shortName, leftX + 8, 111, {
        size: 13.2,
        color: COLORS.orange,
        fontStyle: "bold",
        maxWidth: leftW - 16,
      });

      let infoY = 121;

      infoY += drawInfoLine(
        pdf,
        "Adresse",
        settings?.adresse_club,
        leftX + 8,
        infoY,
        leftW - 16,
      );

      infoY += drawInfoLine(
        pdf,
        "Responsable",
        settings?.responsable_club,
        leftX + 8,
        infoY,
        leftW - 16,
      );

      infoY += drawInfoLine(
        pdf,
        "Téléphone",
        formatPhoneFR(settings?.tel_club),
        leftX + 8,
        infoY,
        leftW - 16,
      );

      infoY += drawInfoLine(
        pdf,
        "Email",
        settings?.mail_club,
        leftX + 8,
        infoY,
        leftW - 16,
      );

      infoY += drawInfoLine(
        pdf,
        "Site",
        displaySiteUrl,
        leftX + 8,
        infoY,
        leftW - 16,
      );

      // SAISON
      if (season) {
        addRoundedRect(
          pdf,
          leftX,
          156,
          leftW,
          13,
          5,
          COLORS.softOrange,
          "#fed7aa",
        );

        addText(pdf, "Saison", leftX + 8, 164.5, {
          size: 9.8,
          color: COLORS.blue,
          fontStyle: "bold",
        });

        addText(pdf, season, leftX + 25, 164.5, {
          size: 9.8,
          color: COLORS.orange,
          fontStyle: "bold",
        });
      }

      // FONCTIONNALITÉS
      addRoundedRect(
        pdf,
        leftX,
        173,
        leftW,
        93,
        7,
        COLORS.white,
        COLORS.border,
      );

      addText(pdf, "Fonctionnalités adhérents", leftX + 8, 185, {
        size: 12.3,
        color: COLORS.blue,
        fontStyle: "bold",
      });

      let featureY = 196;

      features.forEach((feature, index) => {
        drawFeatureRow(
          pdf,
          index,
          feature.label,
          leftX + 8,
          featureY,
          leftW - 16,
        );

        featureY += 7.5;
      });

      // QR CARD
      addRoundedRect(pdf, rightX, 100, rightW, 92, 8, COLORS.blue, COLORS.blue);

      addText(pdf, "Scannez pour", rightX + 7, 113, {
        size: 12.3,
        color: COLORS.white,
        fontStyle: "bold",
      });

      addText(pdf, "créer votre compte", rightX + 7, 121, {
        size: 12.3,
        color: COLORS.white,
        fontStyle: "bold",
      });

      pdf.setFillColor(COLORS.orange);
      pdf.roundedRect(rightX + 7, 125, 27, 1.7, 1, 1, "F");

      const qrBoxSize = rightW - 14;
      const qrBoxX = rightX + 7;
      const qrBoxY = 137;

      addRoundedRect(
        pdf,
        qrBoxX,
        qrBoxY,
        qrBoxSize,
        qrBoxSize,
        5,
        COLORS.white,
        COLORS.white,
      );

      pdf.addImage(
        qrDataUrl,
        "PNG",
        qrBoxX + 3,
        qrBoxY + 3,
        qrBoxSize - 6,
        qrBoxSize - 6,
      );

      // Optionnel : URL sous le QR code, actuellement masquée pour garder un design épuré
      // addText(pdf, registerDisplayUrl, rightX + rightW / 2, 188, {
      //   size: 5.2,
      //   color: COLORS.white,
      //   fontStyle: "bold",
      //   maxWidth: rightW - 10,
      //   align: "center",
      // });

      // ÉTAPES
      addRoundedRect(
        pdf,
        rightX,
        198,
        rightW,
        68,
        7,
        COLORS.white,
        COLORS.border,
      );

      addText(pdf, "En quelques minutes", rightX + 7, 211, {
        size: 10.5,
        color: COLORS.blue,
        fontStyle: "bold",
      });

      const steps = [
        "Créez votre compte parent ou adhérent.",
        "Ajoutez votre dossier d’inscription.",
        "Suivez les informations du club.",
      ];

      let stepY = 224;

      steps.forEach((step, index) => {
        const circleX = rightX + 10;
        const circleY = stepY;

        pdf.setFillColor(COLORS.orange);
        pdf.circle(circleX, circleY, 3.6, "F");

        addText(pdf, String(index + 1), circleX, circleY + 1.9, {
          size: 6,
          color: COLORS.white,
          fontStyle: "bold",
          align: "center",
        });

        addText(pdf, step, rightX + 17, circleY - 1.5, {
          size: 6.7,
          color: COLORS.text,
          fontStyle: "bold",
          maxWidth: rightW - 23,
          lineHeight: 1.12,
        });

        stepY += 14;
      });

      // MESSAGE SÉCURITÉ
      addRoundedRect(pdf, 12, 271, 186, 12, 5, COLORS.softOrange, "#fed7aa");

      addText(pdf, "Simple, rapide et sécurisé", 20, 279, {
        size: 9.2,
        color: COLORS.orange2,
        fontStyle: "bold",
      });

      addText(
        pdf,
        "Vos informations restent dans l’espace numérique du club.",
        82,
        279,
        {
          size: 7.2,
          color: COLORS.text,
          fontStyle: "bold",
        },
      );

      // SIGNATURE BAS DE PAGE
      addText(pdf, "Une application", 62, 291.5, {
        size: 8,
        color: COLORS.muted,
        fontStyle: "bold",
      });

      addText(pdf, "MonAppli", 88, 291.5, {
        size: 8.5,
        color: COLORS.blue,
        fontStyle: "bold",
      });

      addText(pdf, "Club", 106, 291.5, {
        size: 8.5,
        color: COLORS.orange,
        fontStyle: "bold",
      });

      addText(pdf, "créée par Laurent VICTORIN", 119, 291.5, {
        size: 8,
        color: COLORS.muted,
        fontStyle: "bold",
      });

      const fileName = safeFileName(
        `affiche-inscription-${
          settings?.shortname_club || settings?.nom_club || "club"
        }`,
      );

      pdf.save(`${fileName}.pdf`);

      toast.success("Affiche PDF générée.");
    } catch (error) {
      console.error("Erreur génération affiche PDF :", error);
      toast.error("Erreur lors de la génération de l’affiche PDF.");
    } finally {
      setIsGeneratingPoster(false);
    }
  }, [settings, registerUrl, siteUrl]);

  return {
    generatePosterPdf,
    isGeneratingPoster,
  };
}
